import { Banner, Box, Typography } from '@oresundsbron/bridge-ui';
import { TicketTypes } from '@oresundsbron/validators';
import { isSunday, isThursday, nextSunday, previousThursday } from 'date-fns';
import { da, de, enGB, sv } from 'date-fns/locale';
import { useTranslation } from 'next-i18next';
import {
  Dispatch,
  forwardRef,
  RefObject,
  SetStateAction,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import { useIsMobile } from '~/hooks/useIsMobile';

import { useRouter } from 'next/router';
import {
  DateRange,
  DayPicker,
  DayPickerProps,
  DayPickerRangeProps,
  DayPickerSingleProps,
} from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import { toInputDate } from '~/lib/dates';

type DayPickerComponentProps = {
  ticketType?: TicketTypes;
  setFieldData: (
    name: string,
    value: string,
    type: string,
    updateIsDirty?: boolean
  ) => void;
  minDate?: string | null;
  maxDate?: string | null;
  hasValidDates: boolean;
  setHasValidDates: Dispatch<SetStateAction<boolean>>;
  divRef?: RefObject<HTMLDivElement>;
};

export interface DayPickerComponentRef {
  setSelectedDays: Dispatch<SetStateAction<DateRange | Date | undefined>>;
}

export const DayPickerComponent = forwardRef<
  DayPickerComponentRef,
  DayPickerComponentProps
>(
  (
    {
      ticketType,
      setFieldData,
      minDate,
      maxDate,
      hasValidDates,
      setHasValidDates,
      divRef,
    },
    ref
  ) => {
    const { t } = useTranslation(['onlineTicket', 'common'], {
      nsMode: 'fallback',
    });

    const { locale } = useRouter();
    const isMobile = useIsMobile();

    const [selectedDays, setSelectedDays] = useState<DateRange | Date>();

    useImperativeHandle(
      ref,
      () => {
        return {
          setSelectedDays,
        };
      },
      []
    );

    const singleDayCampaignClick = (day?: Date) => {
      setSelectedDays(day);
      if (day) {
        setFieldData('fromDate', toInputDate(day), '');
        setFieldData('toDate', toInputDate(day), '');
        setHasValidDates(true);
      } else {
        setHasValidDates(false);
      }
    };

    const weekendCampaignDayClick = (day: Date) => {
      const thursday: Date = isThursday(day) ? day : previousThursday(day);
      const sunday: Date = isSunday(day) ? day : nextSunday(day);

      setFieldData('fromDate', toInputDate(thursday), '');
      setFieldData('toDate', toInputDate(sunday), '');
      setSelectedDays({
        from: thursday,
        to: sunday,
      });
      setHasValidDates(true);
    };

    const weekendCampaignIsDisabled = (day: Date) => {
      const dayOfWeek = day.getDay();
      return dayOfWeek >= 1 && dayOfWeek <= 3;
    };

    const css = `
       .rdp {
         --rdp-background-color: var(--colors-blue-200);
         --rdp-accent-color: var(--colors-blue-600);
         --rdp-cell-size: 45px;
       }
    `;

    const currentDate = new Date();
    let fromDate: Date = currentDate;
    let toDate: Date = currentDate;
    if (minDate) {
      const campaignStartDate = new Date(minDate);
      fromDate =
        campaignStartDate < currentDate ? currentDate : campaignStartDate;
    }
    if (maxDate) {
      const campaignEndDate = new Date(maxDate);
      toDate = campaignEndDate;
    }

    const dayRangeProps: DayPickerRangeProps = {
      mode: 'range',
      selected: selectedDays as DateRange,
      onDayClick: weekendCampaignDayClick,
      disabled: weekendCampaignIsDisabled,
    };
    const [calendarLocale, setCalendarLocale] = useState(enGB);
    useEffect(() => {
      const localeMap = {
        sv: sv,
        da: da,
        en: enGB,
        de: de,
      };

      const routerLocale = locale || 'en';
      setCalendarLocale(localeMap[routerLocale as keyof typeof localeMap]);
    }, [locale]);

    const daySingleProps: DayPickerSingleProps = {
      mode: 'single',
      selected: selectedDays as Date,
      onSelect: singleDayCampaignClick,
      required: true,
    };
    const isRangeCampaign = ticketType === 'Campaign: Weekend';
    const dayDefaultProps: DayPickerProps = {
      fromDate: fromDate,
      toDate: toDate,
      showOutsideDays: true,
      numberOfMonths: isMobile ? 1 : 2,
      locale: calendarLocale,
      modifiersClassNames: {
        day: '!text-red-600',
        range_middle: '!bg-blue-600 !rounded-none !text-white !rounded-r-none',
        range_start: '!bg-blue-600 !rounded-l-sm !text-white !rounded-r-none',
        range_end: '!bg-blue-600 !rounded-r-sm !text-white !rounded-l-none',
      },
    };

    const chosenProps = isRangeCampaign
      ? { ...dayRangeProps, selected: selectedDays as DateRange }
      : { ...daySingleProps, selected: selectedDays as Date };

    // Merge chosen props with default props
    const mergedProps: DayPickerProps = { ...dayDefaultProps, ...chosenProps };

    if (!minDate || !maxDate) return null;
    return (
      <Box className="mb-16" ref={divRef}>
        <Typography intent="title" as={'h2'} className="pb-4">
          {t('daypicker.title')}
        </Typography>
        <style>{css}</style>
        <Box className="w-max rounded-lg border border-neutral-200">
          <DayPicker {...mergedProps} />
        </Box>

        {!hasValidDates && (
          <Banner color="negative" arrow className="w-max">
            {t('form.validations.datePicker.required')}
          </Banner>
        )}
      </Box>
    );
  }
);
DayPickerComponent.displayName = 'DayPickerComponent';
